/**
 * ReportFilterToolbar Component
 *
 * Toolbar mirroring ClientFilterToolbar visual language, adapted for reports.
 * Provides: debounced search, multi-column filter dropdown (client, status),
 * view switcher (table/grid), and active filter chips. Sorting is driven by
 * column headers, not this toolbar.
 */

import { useEffect, useRef, useState } from "react";
import {
  Input,
  Button,
  cn,
} from "@provenleap/ui";
import {
  MagnifyingGlass,
  X,
  Funnel,
  Check,
  CaretDown,
  User,
  Tag,
  GridFour,
  Table,
  Files,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "motion/react";
import { ClientAvatar } from "../clients/ClientAvatar";
import { ColumnVisibilityMenu } from "../shared/columnVisibility";
import { REPORT_HIDEABLE_COLUMNS } from "./reportColumns";

// ============================================================================
// Types
// ============================================================================

export type ReportStatus = "draft" | "ready";

export type ReportViewMode = "table" | "grid";

interface ReportFilterToolbarProps {
  // Search
  searchQuery: string;
  onSearchChange: (q: string) => void;

  // Filter: by client
  availableClients: Array<{ id: string; name: string; logoUrl?: string | null }>;
  selectedClientIds: string[];
  onClientFilterChange: (clientIds: string[]) => void;

  // Filter: by status
  selectedStatuses: ReportStatus[];
  onStatusFilterChange: (statuses: ReportStatus[]) => void;

  // View switcher
  view: ReportViewMode;
  onViewChange: (view: ReportViewMode) => void;

  // Stats display

  // New Report CTA (inline with view switcher)
  canManageReports?: boolean;
  onNewReport?: () => void;
  newReportDisabled?: boolean;

  // Column-visibility (table view only). Hidden column ids + toggle handler.
  hiddenColumns?: string[];
  onToggleColumn?: (id: string) => void;
  /** Show the "Columns" control — only relevant in the table view. */
  showColumnsControl?: boolean;
}

type FilterTab = "client" | "status";

const STATUS_OPTIONS: Array<{
  value: ReportStatus;
  label: string;
  badgeClass: string;
}> = [
  { value: "draft", label: "Draft", badgeClass: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300" },
  {
    value: "ready",
    label: "Ready",
    badgeClass: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  },
];

// Debounce search input by 200ms to match smooth typing UX while keeping prop
// updates throttled for downstream filtering work.
const SEARCH_DEBOUNCE_MS = 200;

// ============================================================================
// Component
// ============================================================================

export function ReportFilterToolbar({
  searchQuery,
  onSearchChange,
  availableClients,
  selectedClientIds,
  onClientFilterChange,
  selectedStatuses,
  onStatusFilterChange,
  view,
  onViewChange,
  canManageReports,
  onNewReport,
  newReportDisabled,
  hiddenColumns = [],
  onToggleColumn,
  showColumnsControl = false,
}: ReportFilterToolbarProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<FilterTab>("client");
  const [filterSearch, setFilterSearch] = useState("");

  // Debounced search — internal state mirrors prop, emits onSearchChange after delay
  const [searchInternal, setSearchInternal] = useState(searchQuery);
  const lastEmittedRef = useRef(searchQuery);

  // Sync prop -> internal if it changes externally (e.g. clear-all)
  useEffect(() => {
    if (searchQuery !== lastEmittedRef.current) {
      setSearchInternal(searchQuery);
      lastEmittedRef.current = searchQuery;
    }
  }, [searchQuery]);

  useEffect(() => {
    if (searchInternal === lastEmittedRef.current) return;
    const id = setTimeout(() => {
      lastEmittedRef.current = searchInternal;
      onSearchChange(searchInternal);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [searchInternal, onSearchChange]);

  const totalFilterCount =
    selectedClientIds.length + selectedStatuses.length;

  const tabs: { key: FilterTab; label: string; count: number }[] = [
    { key: "client", label: "Client", count: selectedClientIds.length },
    { key: "status", label: "Status", count: selectedStatuses.length },
  ];

  const toggleClient = (id: string) => {
    if (selectedClientIds.includes(id)) {
      onClientFilterChange(selectedClientIds.filter((c) => c !== id));
    } else {
      onClientFilterChange([...selectedClientIds, id]);
    }
  };

  const toggleStatus = (status: ReportStatus) => {
    if (selectedStatuses.includes(status)) {
      onStatusFilterChange(selectedStatuses.filter((s) => s !== status));
    } else {
      onStatusFilterChange([...selectedStatuses, status]);
    }
  };

  const clearAll = () => {
    onClientFilterChange([]);
    onStatusFilterChange([]);
  };

  const nextView: ReportViewMode = view === "grid" ? "table" : "grid";
  const ViewIcon = view === "grid" ? GridFour : Table;
  const viewLabel = view === "grid" ? "Grid" : "Table";

  return (
    <div className="mb-4 space-y-2">
      {/* Row 1: Search + Filter + Count + View */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search input */}
        <div className="relative max-w-xs flex-1">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground"
          />
          <Input
            value={searchInternal}
            onChange={(e) => setSearchInternal(e.target.value)}
            placeholder="Search reports by name..."
            aria-label="Search reports"
            className="pl-9 pr-9 rounded-2xl text-foreground placeholder:text-foreground shadow-xs text-[13.5px]"
            style={{ border: "0.5px solid var(--hairline)" }}
          />
          {searchInternal && (
            <button
              type="button"
              onClick={() => {
                setSearchInternal("");
                lastEmittedRef.current = "";
                onSearchChange("");
              }}
              className="absolute right-2 top-1/2 -translate-y-1/2 min-h-11 min-w-11 flex items-center justify-center text-foreground/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Columns dropdown — table view only (show/hide table columns) */}
        {showColumnsControl && onToggleColumn && (
          <ColumnVisibilityMenu
            columns={REPORT_HIDEABLE_COLUMNS}
            hiddenColumns={hiddenColumns}
            onToggleColumn={onToggleColumn}
          />
        )}

        {/* Filters dropdown */}
        <div className="relative">
          <Button
            variant="outline"
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => {
              setFilterOpen(!filterOpen);
              setFilterSearch("");
            }}
          >
            <Funnel size={16} className="mr-1" />
            Filters
            {totalFilterCount > 0 && (
              <span className="ml-1 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {totalFilterCount}
              </span>
            )}
            <CaretDown size={12} className="ml-1" />
          </Button>

          <AnimatePresence>
            {filterOpen && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => {
                    setFilterOpen(false);
                    setFilterSearch("");
                  }}
                />

                {/* Dropdown panel */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: -4 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: -4 }}
                  transition={{ duration: 0.15, ease: "easeOut" }}
                  className="absolute left-0 top-full mt-2.5 z-50 w-72 bg-popover rounded-2xl shadow-md p-2"
                  style={{ border: "0.5px solid var(--hairline)" }}
                >
                  {/* Arrow pointing up */}
                  <div
                    className="absolute -top-[5px] left-6 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                    style={{
                      borderLeft: "0.5px solid var(--hairline)",
                      borderTop: "0.5px solid var(--hairline)",
                    }}
                  />
                  {/* Tab bar */}
                  <div className="flex mx-2 pb-0 overflow-x-auto">
                    {tabs.map((tab) => (
                      <button
                        key={tab.key}
                        type="button"
                        onClick={() => {
                          setActiveTab(tab.key);
                          setFilterSearch("");
                        }}
                        className={cn(
                          "flex-1 px-2 py-2.5 text-[12.5px] font-medium transition-colors cursor-pointer whitespace-nowrap",
                          activeTab === tab.key
                            ? "text-foreground"
                            : "text-muted-foreground hover:text-foreground"
                        )}
                      >
                        {tab.label}
                        {tab.count > 0 && (
                          <span className="ml-1 inline-flex items-center justify-center bg-primary text-white text-[10px] rounded-full w-4 h-4">
                            {tab.count}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>

                  {/* Search (only meaningful for client tab) */}
                  {activeTab === "client" && (
                    <div className="px-2 pt-2 pb-0.5">
                      <div className="relative">
                        <MagnifyingGlass
                          size={16}
                          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
                        />
                        <input
                          type="text"
                          value={filterSearch}
                          onChange={(e) => setFilterSearch(e.target.value)}
                          placeholder="Search clients..."
                          className="w-full pl-8 pr-3 py-2 text-[13px] text-foreground bg-transparent placeholder:text-muted-foreground placeholder:font-normal font-medium rounded-lg border-[0.5px] border-input focus:outline-none focus-visible:outline-none"
                          style={{ outline: "none", boxShadow: "none" }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Tab content */}
                  <div className="max-h-[300px] overflow-y-auto pt-2 pb-1">
                    {/* Client tab */}
                    {activeTab === "client" && (
                      <>
                        {availableClients.length === 0 ? (
                          <div className="px-3 py-4 text-sm text-muted-foreground text-center">
                            No clients available
                          </div>
                        ) : (
                          [...availableClients]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .filter(
                              (c) =>
                                !filterSearch ||
                                c.name
                                  .toLowerCase()
                                  .includes(filterSearch.toLowerCase())
                            )
                            .map((client) => {
                              const isSelected = selectedClientIds.includes(
                                client.id
                              );
                              return (
                                <button
                                  key={client.id}
                                  type="button"
                                  onClick={() => toggleClient(client.id)}
                                  className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                                >
                                  <span
                                    className={cn(
                                      "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                      isSelected &&
                                        "bg-primary border-primary text-white"
                                    )}
                                  >
                                    {isSelected && <Check size={12} />}
                                  </span>
                                  <ClientAvatar
                                    name={client.name}
                                    logoUrl={client.logoUrl ?? null}
                                    size="sm"
                                    className="!h-5 !w-5 !text-[10px] mr-2"
                                  />
                                  {client.name}
                                </button>
                              );
                            })
                        )}
                      </>
                    )}

                    {/* Status tab */}
                    {activeTab === "status" && (
                      <>
                        {STATUS_OPTIONS.map((opt) => {
                          const isSelected = selectedStatuses.includes(
                            opt.value
                          );
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => toggleStatus(opt.value)}
                              className="flex items-center w-[calc(100%-12px)] ml-1 px-2 py-2 text-[13px] rounded-lg border-[0.9px] border-transparent hover:bg-muted/20 hover:border-input hover:shadow-xs cursor-pointer"
                            >
                              <span
                                className={cn(
                                  "w-4 h-4 border rounded mr-2 flex items-center justify-center shrink-0",
                                  isSelected &&
                                    "bg-primary border-primary text-white"
                                )}
                              >
                                {isSelected && <Check size={12} />}
                              </span>
                              <span
                                className={cn(
                                  "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                                  opt.badgeClass
                                )}
                              >
                                {opt.label}
                              </span>
                            </button>
                          );
                        })}
                      </>
                    )}
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>

        {/* View switcher + New Report — pushed to the right */}
        <div className="flex items-center gap-4 sm:ml-auto">
          <Button
            variant="outline"
            className="shrink-0 rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
            onClick={() => onViewChange(nextView)}
            aria-label={`Switch to ${nextView} view`}
          >
            <ViewIcon size={16} className="mr-1" />
            {viewLabel}
          </Button>

          {canManageReports && onNewReport && (
            <Button
              variant="brand"
              className="shrink-0 rounded-2xl text-[13px] cursor-pointer"
              onClick={onNewReport}
              disabled={newReportDisabled}
            >
              <Files size={16} className="mr-1" />
              Create Report
            </Button>
          )}
        </div>
      </div>

      {/* Row 2: Active filter chips */}
      {totalFilterCount > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          {/* Client chips */}
          {selectedClientIds.map((id) => {
            const client = availableClients.find((c) => c.id === id);
            const name = client?.name ?? id;
            return (
              <span
                key={`client-${id}`}
                className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
              >
                <User size={14} className="shrink-0 text-foreground mr-1.5" />
                {name}
                <button
                  type="button"
                  onClick={() => toggleClient(id)}
                  className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                  style={{ minWidth: 0, minHeight: 0 }}
                  aria-label={`Remove client filter ${name}`}
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          {/* Status chips */}
          {selectedStatuses.map((status) => {
            const opt = STATUS_OPTIONS.find((s) => s.value === status);
            const label = opt?.label ?? status;
            return (
              <span
                key={`status-${status}`}
                className="inline-flex items-center w-fit rounded-2xl bg-background border-[0.5px] border-input px-4 text-[13px] h-10 font-medium text-foreground whitespace-nowrap"
              >
                <Tag size={14} className="shrink-0 text-foreground mr-1.5" />
                {label}
                <button
                  type="button"
                  onClick={() => toggleStatus(status)}
                  className="ml-2.5 text-foreground hover:text-destructive transition-colors leading-none flex items-center shrink-0 cursor-pointer"
                  style={{ minWidth: 0, minHeight: 0 }}
                  aria-label={`Remove status filter ${label}`}
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          <button
            type="button"
            onClick={clearAll}
            className="text-[13px] hover:text-foreground min-h-[44px] px-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
            style={{ color: "#5A7396" }}
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
